import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { TableCell, TableRow, Typography, Tooltip } from '@mui/material';
import { truncateWithEllipsis } from 'utils/action-code';
import { LabAction } from 'models/action';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import { useTranslation } from 'react-i18next';
import { formatMaterialMacroNames } from '../shared/format-material-macro-names';

type ActionItemProps = {
    action: LabAction;
    onActionRowClick: (action: LabAction) => void;
    showInactiveDateCell: boolean;
    showPlanningColumn?: boolean;
    showValueColumn?: boolean;
};

type TemplateCountCellProps = {
    names: (string | null | undefined)[];
    countLabel: (count: number) => string;
    emptyLabel: string;
};

const TemplateCountCell: FC<TemplateCountCellProps> = ({ names, countLabel, emptyLabel }) => {
    const present = names.filter(Boolean) as string[];
    if (!present.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyLabel}
            </Typography>
        );
    }
    return (
        <Tooltip
            title={
                <>
                    {present.map((name, i) => (
                        <div key={i}>{name}</div>
                    ))}
                </>
            }
            placement="top"
        >
            <Typography variant="body2" color="text.primary" sx={{ cursor: 'default' }}>
                {countLabel(present.length)}
            </Typography>
        </Tooltip>
    );
};

const ActionItem: FC<ActionItemProps> = ({ action, onActionRowClick, showInactiveDateCell }: ActionItemProps) => {
    const { t: translate } = useTranslation(['actions-materials', 'restoration-types']);

    const formatGroupNames = () => {
        if (action.groups?.length > 0) {
            return action.groups.map((group) => group?.name).join(' | ');
        }
        return '-';
    };

    const planNames = [action.productionPlanningTemplateName?.digital, action.productionPlanningTemplateName?.physical];
    const valueNames = [action.productionValueTemplateName?.digital, action.productionValueTemplateName?.physical];

    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onActionRowClick(action)}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">{action.actionCode}</Typography>
                {action.internalCode ? (
                    <Tooltip title={action.internalCode} placement="bottom-start">
                        <Typography variant="caption" color="text.secondary" display="block">
                            {truncateWithEllipsis(action.internalCode, 20)}
                        </Typography>
                    </Tooltip>
                ) : undefined}
            </TableCell>
            <TableCell>{action.description}</TableCell>
            <TableCell>
                {action.denthubRestorationType ? translate(`restoration-types:${action.denthubRestorationType}`) : '-'}
            </TableCell>
            <TableCell>{formatGroupNames()}</TableCell>
            <TableCell>{formatMaterialMacroNames(action.materialMacros)}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{action.category}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <TemplateCountCell
                    names={planNames}
                    countLabel={(n) => translate('actions-materials:N_PLANS', { count: n })}
                    emptyLabel={translate('actions-materials:NO_PLANS')}
                />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <TemplateCountCell
                    names={valueNames}
                    countLabel={(n) => translate('actions-materials:N_TEMPLATES', { count: n })}
                    emptyLabel={translate('actions-materials:NO_VALUE_TEMPLATE')}
                />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {action.addedDate && formatDate(new Date(parseInt(action.addedDate)), 'date')}
            </TableCell>
            {showInactiveDateCell && (
                <TableCell>
                    {action.inactivatedDate && formatDate(new Date(parseInt(action.inactivatedDate)), 'date')}
                </TableCell>
            )}{' '}
            <TableCell sx={{ width: 10 }} align="center">
                <ActiveIndicator isActive={!action.inactive} />
            </TableCell>
        </TableRow>
    );
};

export default ActionItem;
